import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, Leaf } from "lucide-react";
import axios from "axios";
import { useSiteTexts, t } from "../contexts/SiteTextsContext";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Social media icons as SVG components
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" strokeWidth={0}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" strokeWidth={0}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" strokeWidth={0}>
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
  </svg>
);

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" strokeWidth={0}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function Footer() {
  const texts = useSiteTexts();
  const [categories, setCategories] = useState([]);
  const [socialLinks, setSocialLinks] = useState({
    instagram_url: "",
    tiktok_url: "",
    facebook_url: "",
    pinterest_url: "",
    whatsapp_url: ""
  });

  useEffect(() => {
    axios.get(`${API_BASE}/settings/social-links`).then(r => setSocialLinks(r.data)).catch(() => {});
    fetch(`${API_BASE}/settings/categories`).then(r => r.json()).then(d => setCategories(d.categories || [])).catch(() => {});
  }, []);

  // Dynamic browse links: 1 parent → subcategories, >1 → parent names
  const enabledParents = categories.filter(c => c.enabled !== false);
  const singleParentMode = enabledParents.length === 1 && enabledParents[0].children?.filter(c => c.enabled !== false).length > 0;
  const singleParent = singleParentMode ? enabledParents[0] : null;
  const browseItems = singleParentMode
    ? enabledParents[0].children.filter(c => c.enabled !== false)
    : enabledParents;

  const buildBrowseUrl = (cat) => {
    if (singleParentMode && singleParent) {
      return `/${singleParent.slug || singleParent.id}/${cat.slug || cat.id}`;
    }
    return `/${cat.slug || cat.id}`;
  };

  const socialIcons = [
    { key: "instagram_url", icon: Instagram, label: "Instagram" },
    { key: "tiktok_url", icon: TikTokIcon, label: "TikTok" },
    { key: "facebook_url", icon: FacebookIcon, label: "Facebook" },
    { key: "pinterest_url", icon: PinterestIcon, label: "Pinterest" },
    { key: "whatsapp_url", icon: WhatsAppIcon, label: "WhatsApp" }
  ];

  const activeSocialLinks = socialIcons.filter(s => socialLinks[s.key]);

  return (
    <footer className="bg-[#6B5B4F] text-white relative overflow-hidden">
      {/* Decorative botanical element */}
      <div className="absolute top-10 right-10 w-24 h-24 opacity-10">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          <ellipse cx="50" cy="50" rx="35" ry="45" fill="#B8C4B8" transform="rotate(-20 50 50)"/>
        </svg>
      </div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-16 py-16 lg:py-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 lg:gap-16">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <Leaf className="w-5 h-5 text-[#B8C4B8]" />
              <span className="text-xl tracking-[0.15em] font-light">MAHRA</span>
            </Link>
            <p className="text-white/50 text-sm leading-relaxed mb-1 font-light">
              {t(texts, "footer_tagline_line1", "Borrow Beautiful.")}
            </p>
            <p className="text-white/50 text-sm leading-relaxed mb-4 font-light">
              {t(texts, "footer_tagline_line2", "Return Happy")}
            </p>
            <p className="text-white/40 text-xs leading-relaxed mb-6 font-light">
              Sustainable celebration fashion from our curated wardrobe to yours.
            </p>
            {/* Dynamic Social Links */}
            {activeSocialLinks.length > 0 && (
              <div className="flex gap-3">
                {activeSocialLinks.map(({ key, icon: Icon, label }) => (
                  <a 
                    key={key}
                    href={socialLinks[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 border border-white/20 rounded-full flex items-center justify-center hover:border-[#9A7B4F] hover:text-[#9A7B4F] transition-colors"
                    aria-label={label}
                    data-testid={`footer-${key}`}
                  >
                    <Icon className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Browse — Dynamic */}
          <div>
            <h4 className="text-[11px] tracking-[0.2em] uppercase mb-6 text-white">Browse</h4>
            <ul className="space-y-3">
              {browseItems.map((cat) => (
                <li key={cat.id}>
                  <Link
                    to={buildBrowseUrl(cat)}
                    className="text-white/50 hover:text-white text-sm transition-colors font-light"
                    data-testid={`footer-browse-${cat.id}`}
                  >
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About */}
          <div>
            <h4 className="text-[11px] tracking-[0.2em] uppercase mb-6 text-white">About</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/our-story" className="text-white/50 hover:text-white text-sm transition-colors font-light">
                  Our Story
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-white/50 hover:text-white text-sm transition-colors font-light">
                  FAQs
                </Link>
              </li>
              <li>
                <Link to="/borrowing-terms" className="text-white/50 hover:text-white text-sm transition-colors font-light">
                  Borrowing Process
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[11px] tracking-[0.2em] uppercase mb-6 text-white">Get in Touch</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-white/50 text-sm font-light">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <span>Oakleigh South, VIC</span>
              </li>
              <li className="flex items-start gap-3 text-white/50 text-sm font-light">
                <Mail className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <Link to="/contact" className="hover:text-white transition-colors">
                  Get in Touch
                </Link>
              </li>
            </ul>
            <Link 
              to="/contact" 
              className="inline-block mt-4 text-[11px] tracking-[0.15em] uppercase text-white/50 hover:text-white transition-colors font-light"
              data-testid="footer-contact-link"
            >
              Contact Us
            </Link>
            <p className="text-white/30 text-xs mt-4 font-light">
              Local pickup & delivery within 15km
            </p>
          </div>
        </div>

        {/* Bottom Bar — All rights, sustainability, privacy flush in one row */}
        <div className="border-t border-white/10 mt-14 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/30 text-xs tracking-wide font-light">
              © {new Date().getFullYear()} Mahra. All rights reserved.
            </p>
            <div className="flex items-center gap-2 text-[#B8C4B8]/80">
              <Leaf className="w-3.5 h-3.5" />
              <p className="text-xs tracking-wide font-light">
                {t(texts, "footer_sustainability", "Every borrow is a step towards sustainable fashion")}
              </p>
            </div>
            <div className="flex gap-6 text-xs tracking-wide">
              <Link to="/privacy-policy" className="text-white/30 hover:text-white/60 transition-colors font-light" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>
              <Link to="/terms-of-use" className="text-white/30 hover:text-white/60 transition-colors font-light" data-testid="footer-terms-link">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
